import { useEffect, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { IReducer } from 'Reducers/IReducer';
import { IDashboard } from 'pages/Dashboard';
import { useNavigate } from 'react-router-dom';
import { getLaboratoryById, resetCurrentLaboratory, getMyTasks, getDashboardStats } from 'actions/LaboratoryActions';
import { getUserList } from 'actions/UserAction';
import Header from 'components/Header/Header';
import { IHeader } from 'components/Header';
import { TaskTable } from './TaskTable';
import { DashboardStats } from './DashboardStats';
import moment from 'moment';

const DATE_RANGE_PRESETS: Record<string, () => { startDate: string; endDate: string }> = {
  this_week: () => ({
    startDate: moment().startOf('week').format('YYYY-MM-DD'),
    endDate: moment().endOf('week').format('YYYY-MM-DD'),
  }),
  last_week: () => ({
    startDate: moment().subtract(1, 'week').startOf('week').format('YYYY-MM-DD'),
    endDate: moment().subtract(1, 'week').endOf('week').format('YYYY-MM-DD'),
  }),
  this_month: () => ({
    startDate: moment().startOf('month').format('YYYY-MM-DD'),
    endDate: moment().endOf('month').format('YYYY-MM-DD'),
  }),
  last_month: () => ({
    startDate: moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
    endDate: moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD'),
  }),
  last_3_months: () => ({
    startDate: moment().subtract(3, 'months').startOf('month').format('YYYY-MM-DD'),
    endDate: moment().endOf('month').format('YYYY-MM-DD'),
  }),
};

function Dashboard(props: IDashboard) {
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('this_month');

  const navigate = useNavigate();

  const fetchLaboratoryById = async (id: string) => {
    let batch: any[] = [];
    batch.push(props.getLaboratoryById({ id }));
    batch.push(props.getUserList({ laboratoryId: id }));
    await Promise.all(batch);
    setLoading(false);
  };

  useEffect(() => {
    if (props.laboratoryList.length) {
      const labId = props.laboratoryList[0]._id ?? '0';
      fetchLaboratoryById(labId);
      props.getMyTasks(labId);
      const dates = DATE_RANGE_PRESETS[dateRange]();
      props.getDashboardStats(labId, dates);
    } else {
      props.resetCurrentLaboratory();
      navigate('/laboratory/notfound');
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (props.laboratoryList.length) {
      const labId = props.laboratoryList[0]._id ?? '0';
      const dates = DATE_RANGE_PRESETS[dateRange]();
      props.getDashboardStats(labId, dates);
    }
    // eslint-disable-next-line
  }, [dateRange]);

  const handleDateRangeChange = (value: string) => {
    setDateRange(value);
  };

  const breadcrumbsList: IHeader['breadcrumbsList'] = [
    {
      label: 'Dashboard',
      onClick: () => navigate('/'),
    },
  ];

  return loading ? (
    <Box display={'flex'} flexDirection={'column'} alignItems={'center'} justifyContent={'center'} height={'100%'}>
      <CircularProgress />
    </Box>
  ) : (
    <Box>
      <Header breadcrumbsList={breadcrumbsList} title="Dashboard" />
      <Box pt={10}>
        <DashboardStats
          dashboardStats={props.dashboardStats}
          dateRange={dateRange}
          onDateRangeChange={handleDateRangeChange}
        />
        <Typography variant="h6" fontWeight={600} mb={1}>
          My Pending Tasks
        </Typography>
        <TaskTable data={props.tasks} />
      </Box>
    </Box>
  );
}

const mapStateToProps = (state: IReducer) => ({
  user: state.auth.user,
  laboratoryList: state.laboratory.list,
  laboratory: state.laboratory.current,
  tasks: state.laboratory.tasks,
  dashboardStats: state.laboratory.dashboardStats,
});

function mapDispatchToProps(dispatch: any) {
  return {
    ...bindActionCreators({ getLaboratoryById, resetCurrentLaboratory, getUserList, getMyTasks, getDashboardStats }, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(Dashboard);
